import { Link, useNavigate } from 'react-router-dom';
import { CaretDown, SignOut, List, UserCircle } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useState, useRef, useEffect } from 'react';
import MobileMenu from './MobileMenu';
import { useViewport } from '../../hooks/useViewport';

interface HeaderProps {
  variant?: 'default' | 'auth';
  onContactSupport?: () => void;
}

export default function Header({ variant = 'default', onContactSupport }: HeaderProps) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewport();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle tour events for opening/closing mobile menu
  useEffect(() => {
    const handleOpenMobileMenu = () => {
      if (isMobile) {
        setIsMobileMenuOpen(true);
      }
    };

    const handleCloseMobileMenu = () => {
      if (isMobile) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('tour:open-mobile-menu', handleOpenMobileMenu);
    window.addEventListener('tour:close-mobile-menu', handleCloseMobileMenu);

    return () => {
      window.removeEventListener('tour:open-mobile-menu', handleOpenMobileMenu);
      window.removeEventListener('tour:close-mobile-menu', handleCloseMobileMenu);
    };
  }, [isMobile]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const Logo = () => (
    <a href="https://app.educaite.co" className="flex items-center gap-4 text-chalk-white">
      <div className="h-6 md:h-8 w-auto">
        <img
          src="/logo.png"
          alt="EducAIte Logo"
          className="h-full w-auto object-contain"
        />
      </div>
    </a>
  );

  const AuthNav = () => (
    <div className="flex flex-1 justify-end gap-8">
      <Link
        to="/signup"
        className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded h-10 px-4 bg-chalk-blue text-blackboard text-sm font-bold leading-normal tracking-[0.015em] hover:bg-opacity-90 transition-all"
      >
        <span className="truncate">Sign up</span>
      </Link>
    </div>
  );

  const MainNav = () => (
    <div className="flex flex-1 justify-end gap-8">
      {!isMobile && (
        <div className="flex items-center gap-9">
          <Link to="/dashboard" className="text-chalk-white text-sm font-medium leading-normal hover:text-chalk-blue transition-colors">
            Dashboard
          </Link>
          <Link to="/history" data-tour="lesson-history-button" className="text-chalk-white text-sm font-medium leading-normal hover:text-chalk-blue transition-colors">
            Lesson History
          </Link>
          <Link to="/progress-reports" className="text-chalk-white text-sm font-medium leading-normal hover:text-chalk-blue transition-colors">
            Progress Reports
          </Link>
        </div>
      )}
      {isAuthenticated && !isMobile && (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {user?.profileImage ? (
              <div
                className="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10 border-2 border-chalk-blue"
                style={{ backgroundImage: `url(${user.profileImage})` }}
              />
            ) : (
              <div className="bg-chalk-blue rounded-full size-10 flex items-center justify-center text-blackboard text-lg font-bold">
                {user?.fullName.charAt(0)}
              </div>
            )}
            <CaretDown 
              size={16} 
              className={`text-chalk-white transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
              <div className="py-1">
                <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100 overflow-hidden">
                  <div className="font-medium truncate">{user?.fullName}</div>
                  <div className="text-gray-500 truncate">{user?.email}</div>
                </div>
                <button
                  onClick={() => { setIsDropdownOpen(false); navigate('/settings/profile'); }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <UserCircle size={16} className="mr-3" />
                  Profile & settings
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <SignOut size={16} className="mr-3" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-border px-4 md:px-10 py-3 bg-blackboard/90 backdrop-blur-sm">
        {/* Hamburger Menu Button - Mobile Only */}
        {variant === 'default' && isAuthenticated && isMobile && (
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="text-chalk-white hover:text-chalk-blue transition-colors p-2 -ml-2"
            aria-label="Open menu"
          >
            <List size={24} />
          </button>
        )}
        
        <Logo />
        {variant === 'auth' ? <AuthNav /> : <MainNav />}
      </header>

      {/* Mobile Menu */}
      {variant === 'default' && isAuthenticated && (
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          onContactSupport={onContactSupport}
        />
      )}
    </>
  );
}
